import React, { useMemo, useEffect } from "react";
import { motion } from "framer-motion";
import {
  TrendingUp,
  Target,
  Shield,
  BarChart3,
  Info,
  CheckCircle2,
  AlertCircle,
  Percent,
} from "lucide-react";

// Scoring weights (constant)
const SCORING_WEIGHTS = {
  engagement_quality: 30,
  conversion_performance: 40,
  compliance: 20,
  consistency: 10,
};

const PublisherScoringModel = ({ promoter, channelRatings, riskChecks, onScoringCalculated }) => {
  const weights = SCORING_WEIGHTS;

  // Calculate scores based on available data
  const calculateScores = useMemo(() => {
    // Engagement Quality (30%) - Based on channel ratings
    let engagementScore = 0;
    let engagementMax = 0;
    if (channelRatings && Object.keys(channelRatings).length > 0) {
      const scores = { high: 3, medium: 2, low: 1 };
      Object.values(channelRatings).forEach((ratings) => {
        if (ratings && typeof ratings === "object") {
          Object.values(ratings).forEach((rating) => {
            if (rating) {
              engagementScore += scores[rating] || 0;
              engagementMax += 3;
            }
          });
        }
      });
    }
    const engagementPercentage = engagementMax > 0 ? (engagementScore / engagementMax) * 100 : 0;
    const engagementWeighted = (engagementPercentage * weights.engagement_quality) / 100;

    // Conversion Performance (40%) - Not available at approval stage, default to 0
    // This will be calculated post-activation based on actual performance
    const conversionPercentage = 0; // Placeholder - will be calculated after activation
    const conversionWeighted = (conversionPercentage * weights.conversion_performance) / 100;

    // Compliance (20%) - Based on risk checks
    let complianceScore = 0;
    let complianceMax = 0;
    if (riskChecks && Object.keys(riskChecks).length > 0) {
      Object.values(riskChecks).forEach((passed) => {
        complianceMax += 1;
        if (passed === true) {
          complianceScore += 1;
        }
      });
    }
    const compliancePercentage = complianceMax > 0 ? (complianceScore / complianceMax) * 100 : 0;
    const complianceWeighted = (compliancePercentage * weights.compliance) / 100;

    // Consistency (10%) - Based on profile completeness and channel consistency
    const profileCompleteness = promoter?.profile_completeness || 0;
    const consistencyPercentage = profileCompleteness; // Using profile completeness as proxy
    const consistencyWeighted = (consistencyPercentage * weights.consistency) / 100;

    const totalScore = engagementWeighted + conversionWeighted + complianceWeighted + consistencyWeighted;

    return {
      engagement: {
        percentage: engagementPercentage,
        weighted: engagementWeighted,
        max: weights.engagement_quality,
      },
      conversion: {
        percentage: conversionPercentage,
        weighted: conversionWeighted,
        max: weights.conversion_performance,
      },
      compliance: {
        percentage: compliancePercentage,
        weighted: complianceWeighted,
        max: weights.compliance,
      },
      consistency: {
        percentage: consistencyPercentage,
        weighted: consistencyWeighted,
        max: weights.consistency,
      },
      total: totalScore,
    };
  }, [channelRatings, riskChecks, promoter, weights]);

  const scores = calculateScores;

  // Pass scoring data to parent component for payload
  useEffect(() => {
    if (onScoringCalculated && typeof onScoringCalculated === 'function') {
      const scoringData = {
        engagement_quality: {
          score: scores.engagement.percentage,
          weighted_score: scores.engagement.weighted,
        },
        conversion_performance: {
          score: scores.conversion.percentage,
          weighted_score: scores.conversion.weighted,
        },
        compliance: {
          score: scores.compliance.percentage,
          weighted_score: scores.compliance.weighted,
        },
        consistency: {
          score: scores.consistency.percentage,
          weighted_score: scores.consistency.weighted,
        },
        overall_score: scores.total,
        calculated_at: new Date().toISOString(),
      };
      onScoringCalculated(scoringData);
    }
  }, [scores, onScoringCalculated]);

  const metrics = [
    {
      id: "engagement_quality",
      label: "Engagement Quality",
      weight: weights.engagement_quality,
      icon: Target,
      color: "blue",
      description: "Based on channel audience relevance, engagement rates, and content clarity",
      score: scores.engagement,
      details: "Evaluated from channel quality assessments",
    },
    {
      id: "conversion_performance",
      label: "Conversion Performance",
      weight: weights.conversion_performance,
      icon: TrendingUp,
      color: "green",
      description: "Measured post-activation: valid conversions, click-through rates, and campaign performance",
      score: scores.conversion,
      details: "Will be calculated after activation based on actual campaign performance",
      isPostActivation: true,
    },
    {
      id: "compliance",
      label: "Compliance",
      weight: weights.compliance,
      icon: Shield,
      color: "purple",
      description: "Risk assessment: no spam, no fake followers, no misleading content, no previous abuse",
      score: scores.compliance,
      details: "Based on risk & compliance review",
    },
    {
      id: "consistency",
      label: "Consistency",
      weight: weights.consistency,
      icon: BarChart3,
      color: "orange",
      description: "Profile completeness, posting consistency, and channel maintenance",
      score: scores.consistency,
      details: "Based on profile completeness and channel consistency",
    },
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: {
        bg: "bg-blue-50",
        border: "border-blue-200",
        text: "text-blue-900",
        icon: "text-blue-600",
        iconBg: "bg-blue-100",
        progress: "bg-blue-500",
        progressBg: "bg-blue-100",
      },
      green: {
        bg: "bg-green-50",
        border: "border-green-200",
        text: "text-green-900",
        icon: "text-green-600",
        iconBg: "bg-green-100",
        progress: "bg-green-500",
        progressBg: "bg-green-100",
      },
      purple: {
        bg: "bg-purple-50",
        border: "border-purple-200",
        text: "text-purple-900",
        icon: "text-purple-600",
        iconBg: "bg-purple-100",
        progress: "bg-purple-500",
        progressBg: "bg-purple-100",
      },
      orange: {
        bg: "bg-orange-50",
        border: "border-orange-200",
        text: "text-orange-900",
        icon: "text-orange-600",
        iconBg: "bg-orange-100",
        progress: "bg-orange-500",
        progressBg: "bg-orange-100",
      },
    };
    return colors[color] || colors.blue;
  };

  const getScoreGrade = (totalScore) => {
    if (totalScore >= 80) return { label: "Excellent", color: "green" };
    if (totalScore >= 60) return { label: "Good", color: "blue" };
    if (totalScore >= 40) return { label: "Fair", color: "yellow" };
    return { label: "Needs Improvement", color: "red" };
  };

  const grade = getScoreGrade(scores.total);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="p-6 bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 rounded-xl border-2 border-indigo-200">
        <div className="flex gap-4 items-start">
          <div className="p-3 bg-indigo-100 rounded-xl">
            <BarChart3 className="w-6 h-6 text-indigo-600" />
          </div>
          <div className="flex-1">
            <h3 className="mb-2 text-2xl font-bold text-gray-900">
              Publisher Scoring Model
            </h3>
            <p className="mb-4 text-sm text-gray-700">
              Comprehensive evaluation system to assess publisher quality and performance potential.
              Scores are calculated using weighted metrics based on available data.
            </p>
            <div className="flex gap-4 items-center">
              <div className="p-3 bg-white rounded-lg border border-indigo-200">
                <div className="flex gap-2 items-baseline">
                  <span className="text-3xl font-bold text-indigo-600">
                    {scores.total.toFixed(1)}
                  </span>
                  <span className="text-sm text-gray-500">/ 100</span>
                </div>
                <p className="text-xs text-gray-600 mt-1">Overall Score</p>
              </div>
              <div className="p-3 bg-white rounded-lg border border-indigo-200">
                <div className="flex gap-2 items-center">
                  <span
                    className={`px-3 py-1 text-sm font-semibold rounded-full ${
                      grade.color === "green"
                        ? "bg-green-100 text-green-700"
                        : grade.color === "blue"
                        ? "bg-blue-100 text-blue-700"
                        : grade.color === "yellow"
                        ? "bg-yellow-100 text-yellow-700"
                        : "bg-red-100 text-red-700"
                    }`}
                  >
                    {grade.label}
                  </span>
                </div>
                <p className="text-xs text-gray-600 mt-1">Grade</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scoring Metrics */}
      <div className="space-y-4">
        {metrics.map((metric, index) => {
          const Icon = metric.icon;
          const colors = getColorClasses(metric.color);
          const scorePercentage = metric.score.percentage;

          return (
            <motion.div
              key={metric.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`p-6 ${colors.bg} rounded-xl border-2 ${colors.border} shadow-sm`}
            >
              <div className="flex gap-4 items-start mb-4">
                <div className={`p-3 ${colors.iconBg} rounded-lg`}>
                  <Icon className={`w-6 h-6 ${colors.icon}`} />
                </div>
                <div className="flex-1">
                  <div className="flex gap-3 items-center justify-between mb-2">
                    <div>
                      <h4 className="text-lg font-bold text-gray-900">
                        {metric.label}
                      </h4>
                      <div className="flex gap-2 items-center mt-1">
                        <span className="px-2 py-0.5 text-xs font-semibold text-gray-600 bg-white rounded border border-gray-200">
                          {metric.weight}% Weight
                        </span>
                        {metric.isPostActivation && (
                          <span className="px-2 py-0.5 text-xs font-semibold text-amber-700 bg-amber-100 rounded border border-amber-200">
                            Post-Activation
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex gap-2 items-baseline">
                        <span className={`text-2xl font-bold ${colors.text}`}>
                          {metric.score.weighted.toFixed(1)}
                        </span>
                        <span className="text-sm text-gray-500">/ {metric.score.max}</span>
                      </div>
                      <div className="flex gap-1 items-center mt-1">
                        <Percent className="w-3 h-3 text-gray-400" />
                        <span className="text-xs text-gray-600">
                          {scorePercentage.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  </div>

                  <p className="mb-3 text-sm text-gray-700">{metric.description}</p>

                  {/* Progress Bar */}
                  <div className="mb-3">
                    <div className={`h-3 ${colors.progressBg} rounded-full overflow-hidden`}>
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${scorePercentage}%` }}
                        transition={{ duration: 0.8, delay: index * 0.1 }}
                        className={`h-full ${colors.progress} rounded-full`}
                      />
                    </div>
                  </div>

                  <div className="flex gap-2 items-center text-xs text-gray-600">
                    {metric.isPostActivation ? (
                      <>
                        <AlertCircle className="w-4 h-4 text-amber-600" />
                        <span className="italic">
                          {metric.details}
                        </span>
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                        <span>{metric.details}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Scoring Breakdown Table */}
      <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
        <h4 className="mb-4 text-lg font-bold text-gray-900">Scoring Breakdown</h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Metric</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-700">Weight</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-700">Score %</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-700">Weighted Score</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {metrics.map((metric) => (
                <tr key={metric.id}>
                  <td className="px-4 py-3 font-medium text-gray-900">
                    {metric.label}
                    {metric.isPostActivation && (
                      <span className="ml-2 text-xs text-amber-600">(Post-Activation)</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center text-gray-600">
                    {metric.weight}%
                  </td>
                  <td className="px-4 py-3 text-center text-gray-600">
                    {metric.score.percentage.toFixed(1)}%
                  </td>
                  <td className="px-4 py-3 text-center font-semibold text-gray-900">
                    {metric.score.weighted.toFixed(1)} / {metric.score.max}
                  </td>
                </tr>
              ))}
              <tr className="bg-gray-50 font-bold">
                <td className="px-4 py-3 text-gray-900">Total Score</td>
                <td className="px-4 py-3 text-center text-gray-600">100%</td>
                <td className="px-4 py-3 text-center text-gray-600">
                  {scores.total.toFixed(1)}%
                </td>
                <td className="px-4 py-3 text-center text-indigo-600">
                  {scores.total.toFixed(1)} / 100
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Info Box */}
      <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
        <div className="flex gap-3 items-start">
          <Info className="w-5 h-5 mt-0.5 text-blue-600 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <p className="font-semibold mb-1">How Scoring Works:</p>
            <ul className="space-y-1 ml-4 list-disc">
              <li>Scores are calculated using weighted metrics based on available data</li>
              <li>Conversion Performance (40%) will be calculated post-activation based on actual campaign results</li>
              <li>Publishers are evaluated monthly after activation for level upgrades</li>
              <li>Consistent positive performance leads to level upgrades and better commission rates</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublisherScoringModel;
